/**
 * Wallet ⇄ SIWE-session lifecycle sync (security).
 *
 * The wagmi wallet connection and the server-side SIWE session cookie are two
 * independent state machines. When the user DISCONNECTS the wallet (from the app
 * or the extension) or SWITCHES accounts (A → B), the old server session must be
 * invalidated immediately — otherwise privileged UI (Admin, Profile) and server
 * capabilities persist until cookie expiry, a real risk on a shared computer.
 *
 * On such a transition we: (1) POST /auth/logout to delete the server session +
 * clear the cookie, (2) drop the in-memory CSRF token, then (3) HARD-RELOAD the
 * page so the app resets to exactly the "no wallet ever connected" state (per
 * Claudio) with zero stale React state anywhere.
 *
 * Uses wagmi v2 `useAccountEffect` whose `onDisconnect` fires ONLY on a real
 * connected → disconnected transition (never on the initial mount or the
 * reconnect-on-mount hydration flicker).
 */
import { useCallback, useEffect, useRef } from "react";
import { useAccount, useAccountEffect } from "wagmi";
import { api } from "../lib/api";
import { clearCsrfToken } from "../lib/territory-identity-api";

export function useWalletSessionSync(): void {
  const { address, status } = useAccount();
  const baseline = useRef<string | undefined>(undefined);
  const stable = useRef(false);
  const inFlight = useRef(false);

  const resetSession = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await api.logout();
    } catch {
      /* ignore network errors — we still clear client state + reload below */
    } finally {
      clearCsrfToken();
      // Full reset: reload so the app is exactly as if no wallet had connected.
      window.location.reload();
    }
  }, []);

  useAccountEffect({
    onConnect({ address }) {
      // Baseline the first stable connection (incl. reconnect-on-mount). Never a
      // logout trigger.
      if (!stable.current) {
        stable.current = true;
        baseline.current = address?.toLowerCase();
      }
    },
    onDisconnect() {
      // wagmi only calls this after a real connected → disconnected transition.
      stable.current = false;
      baseline.current = undefined;
      void resetSession();
    },
  });

  useEffect(() => {
    // Detect an account SWITCH (A → B) that stays "connected". Ignore
    // undefined / connecting / reconnecting so mount + hydration never fire.
    if (status !== "connected" || !address) return;
    const current = address.toLowerCase();
    if (!stable.current) {
      stable.current = true;
      baseline.current = current;
      return;
    }
    if (baseline.current && baseline.current !== current) {
      baseline.current = current;
      void resetSession();
    }
  }, [address, status, resetSession]);
}
